import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Paper,
  Switch,
  Typography,
} from "@mui/material";
import {
  useCreate,
  useDelete,
  useUpdate,
  useInvalidate,
} from "@refinedev/core";
import { useCallback, useEffect, useState } from "react";
import {
  getExpressions,
  ExpressionWithMatch,
  getLaraResources,
  LaraResource,
} from "../../api";
import { brandBatchSchema } from "./validation";
import {
  Batch,
  BatchFolder,
  Brand,
  BrandBatchSettings,
  BulletPointType,
  CommonFormField,
  CreateBatchData,
  CreateBrandData,
  DataSourceMode,
  defaultSettings,
  LanguageCode,
  languageCodeToLabel,
  Monogram,
  UpdateBatchData,
  UpdateBrandData,
} from "../../pages/playground/types";
// import BulletPointsOrder from "./form/bullet-points-order";
import WordsToAvoid from "./form/words-to-avoid";
import Instructions from "./form/instructions";
import BrandInstructions from "./form/brand-instructions";
import GeneralSettings from "./form/general-settings";
import ArchetypeScores from "./form/archetype-scores";
import ExpressionSelector from "./form/expression-selector";
import Monograms from "./form/monograms";
import {
  getBatchesFolders,
  getBrandById,
  unarchiveBrand,
  unarchiveBatch,
} from "../../api/batches";
import useGlobalStore from "../../store";
import PhrasebookBrands from "./form/phrasebook-brands";
import { getBrandsWithGlobalPhrases } from "../../api/phrasebook";
import ContentGroupsSettings from "./form/content-groups-settings";
import ExportRulesSettings from "./form/export-rules-settings";
import { getContentGroups } from "../../utils/content-groups";
import { useAuthStore } from "../../authProvider";
import LanguagesToTranslate from "./form/languages-to-translate";
import LaraLanguages from "./form/lara-languages";
import LaraGlossaries from "./form/lara-glossaries";
import LaraMemories from "./form/lara-memories";
import LaraInstructions from "./form/lara-instructions";
import LaraStyle from "./form/lara-style";
import { GDriveFolderPickerDialog } from "./gdrive-folder-picker-dialog";
import { getTitleTemplatePatterns } from "../../api/multiagents";
import TitleTemplates from "./form/title-templates";
import DataSourceModeSelector from "./form/data-source-mode";

interface ExpressionItem {
  expression: string;
  checked: boolean;
  initialOrder: number; // Added to maintain sort order
  matchPercentage?: number; // Optional match percentage from API
}

interface CommonBatchBrandFormProps {
  type: "brand" | "batch";
  id?: undefined | string;
  data?: Partial<
    CreateBrandData | CreateBatchData | UpdateBrandData | UpdateBatchData
  >;
  onClose: () => void;
}

export const CommonBatchBrandForm = ({
  type,
  id,
  data,
  onClose,
}: CommonBatchBrandFormProps) => {
  const isEdit = !!id;

  const { user } = useAuthStore();

  const isClientRelated = user?.isClientRelated;

  const { mutateAsync: create } = useCreate();
  const { mutateAsync: update } = useUpdate();
  const { mutateAsync: deleteFn } = useDelete();
  const invalidate = useInvalidate();

  const { setShowGDriveLoginError } = useGlobalStore();

  const [initialSettings, setInitialSettings] =
    useState<BrandBatchSettings | null>(null);

  const [commonForm, setCommonForm] = useState<Partial<Brand | Batch>>(() => {
    const defaultData = {
      name: "",
      logo_name: "",
      gdrive_folder_id: "",
      brand_id: "",
      collection_name: "",
      season: "",
      original_language: LanguageCode.IT,
      narrative_perspective: "THIRD_PERSON",
      archetype_score_angelo_custode: 0,
      archetype_score_amante: 0,
      archetype_score_burlone: 0,
      archetype_score_sovrano: 0,
      archetype_score_innocente: 0,
      archetype_score_ribelle: 0,
      archetype_score_uomo_comune: 0,
      archetype_score_creatore: 0,
      archetype_score_mago: 0,
      archetype_score_saggio: 0,
      archetype_score_eroe: 0,
      archetype_score_esploratore: 0,
      words_to_avoid: [],
      bullet_points_order: [],
      monograms: [],
      settings: defaultSettings,
    };

    if (!data) return defaultData;

    // For batch creation, clear gdrive_folder_id (brand's folder doesn't apply)
    if (type === "batch" && !id) {
      return {
        ...data,
        gdrive_folder_id: undefined,
      };
    }

    return data;
  });

  const [brandsWithGlobalPhrases, setBrandsWithGlobalPhrases] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);

  // Data source mode: "gdrive" or "manual"
  // Default is manual, unless editing and gdrive_folder_id exists
  // For batch creation, always start with manual (brand's folder doesn't apply to batch)
  const [dataSourceMode, setDataSourceMode] = useState<DataSourceMode>(() => {
    if (type === "batch" && !isEdit) {
      return "manual";
    }
    return data?.gdrive_folder_id ? "gdrive" : "manual";
  });

  const [availableTitleTemplatePatterns, setAvailableTitleTemplatePatterns] =
    useState<
      {
        name: string;
        value: string;
      }[]
    >([]);

  const [laraResources, setLaraResources] = useState<{
    glossaries: LaraResource[];
    memories: LaraResource[];
  }>({ glossaries: [], memories: [] });
  const [isLoadingLaraResources, setIsLoadingLaraResources] = useState(false);

  const [brand, setBrand] = useState<Brand | null>(null);

  const fetchBrand = async (id: string) => {
    try {
      const response = await getBrandById({ id });
      setBrand(response?.data?.data);
    } catch (error) {
      console.error("Error fetching brand:", error);
    }
  };

  useEffect(() => {
    if (type === "batch" && (data as CreateBatchData)?.brand_id) {
      fetchBrand((data as CreateBatchData)?.brand_id);
    }
  }, [type, data]);

  // AI permission: for brands (admin), use user permission; for batches, use brand's org permission
  const allowAIGeneration =
    type === "brand"
      ? !isClientRelated || user?.mode_allow_ai_generation
      : brand?.organization_mode_allow_ai_generation === true;

  // AI translation permission: for brands (admin), use user permission; for batches, use brand's org permission
  const allowLaraTranslation =
    type === "brand"
      ? !isClientRelated || user?.mode_allow_translate_ai
      : brand?.organization_mode_allow_translate_ai === true;

  // Human translation permission: for brands (admin), use user permission; for batches, use brand's org permission
  const allowTranslations =
    type === "brand"
      ? !isClientRelated || user?.mode_allow_translate
      : brand?.organization_mode_allow_translate === true;

  useEffect(() => {
    const fetchTitleTemplatePatterns = async () => {
      try {
        const response = await getTitleTemplatePatterns();
        setAvailableTitleTemplatePatterns(response?.data?.data?.patterns || []);
      } catch (error) {
        console.error("Error fetching title template patterns:", error);
      }
    };

    fetchTitleTemplatePatterns();
  }, []);

  useEffect(() => {
    // Only fetch lara resources for non-client users
    if (isClientRelated) {
      return;
    }

    const fetchLaraResources = async () => {
      setIsLoadingLaraResources(true);
      try {
        const resources = await getLaraResources();
        setLaraResources({
          glossaries: resources.glossaries || [],
          memories: resources.memories || [],
        });
      } catch (error) {
        console.error("Error fetching Lara resources:", error);
      } finally {
        setIsLoadingLaraResources(false);
      }
    };

    fetchLaraResources();
  }, [isClientRelated]);

  const fetchBrandsWithGlobalPhrases = async (organizationId?: string) => {
    try {
      const response = await getBrandsWithGlobalPhrases(organizationId);

      const brands = response?.data as unknown as {
        id: string;
        name: string;
      }[];

      setBrandsWithGlobalPhrases(brands);

      if (isClientRelated && type === "batch" && !isEdit) {
        const newSettings = {
          ...(commonForm.settings || defaultSettings),
          phrasebook_brands: brands.map((brand) => brand.id),
        };

        setCommonForm({
          ...commonForm,
          settings: newSettings,
        });
      }
    } catch (error) {
      console.error("Error fetching brands with global phrases:", error);
    }
  };

  // Fetch brands when brand is loaded (for batch editing with organization filtering)
  useEffect(() => {
    // For admins/managers editing a batch with a brand that has an organization,
    // filter phrasebook brands by that organization
    const organizationId =
      !isClientRelated && type === "batch" && brand?.organization_id
        ? brand.organization_id
        : undefined;
    fetchBrandsWithGlobalPhrases(organizationId);

    // note: do not add fetchBrandsWithGlobalPhrases,
    // as it incurs in endless loop
  }, [brand?.organization_id, isClientRelated, type]);

  useEffect(() => {
    if (data?.settings && isEdit) {
      setInitialSettings(JSON.parse(JSON.stringify(data.settings)));
    }
  }, [data, isEdit]);

  // Clean up duplicate languages on initial load (priority to human translations)
  useEffect(() => {
    const humanLanguages = commonForm.settings?.languages_to_translate || [];
    const aiLanguages = commonForm.settings?.lara_languages || [];

    if (humanLanguages.length > 0 && aiLanguages.length > 0) {
      // Find languages that are in both lists
      const duplicates = aiLanguages.filter((lang) =>
        humanLanguages.includes(lang),
      );

      if (duplicates.length > 0) {
        // Remove duplicates from AI languages (priority to human translations)
        const cleanedAiLanguages = aiLanguages.filter(
          (lang) => !humanLanguages.includes(lang),
        );

        setCommonForm((prev) => ({
          ...prev,
          settings: {
            ...(prev.settings || defaultSettings),
            lara_languages: cleanedAiLanguages,
          },
        }));
      }
    }
    // Only run once on initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [batchesFolders, setBatchesFolders] = useState<BatchFolder[]>([]);
  const [showFolderDialog, setShowFolderDialog] = useState(false);

  useEffect(() => {
    // disable batches folders for clients or when using manual mode
    if (isClientRelated || dataSourceMode === "manual") {
      return;
    }

    const fetchBatchesFolders = async () => {
      try {
        const response = await getBatchesFolders({
          folderId: brand?.gdrive_folder_id,
        });

        const data = response?.data;

        if (response.status !== 200 || data?.status === "ERROR") {
          setShowGDriveLoginError(true);
        } else {
          setBatchesFolders(data?.data?.batches_folders || []);
        }
      } catch (error) {
        console.error("Error fetching batches folders:", error);
        setShowGDriveLoginError(true);
      }
    };

    if (type === "batch" && brand) {
      const brandFolderId = brand.gdrive_folder_id;

      // Only fetch folders if brand has a folder ID
      if (brandFolderId) {
        fetchBatchesFolders();
      }
    } else if (type === "brand") {
      fetchBatchesFolders();
    }
  }, [
    type,
    brand,
    setShowGDriveLoginError,
    isClientRelated,
    isEdit,
    dataSourceMode,
  ]);

  const [firstFetchedExpressions, setFirstFetchedExpressions] =
    useState<boolean>(false);

  const [expressions, setExpressions] = useState<ExpressionItem[]>(
    (data?.expressions || []).map((expression, index) => ({
      expression,
      checked: true,
      initialOrder: index,
    })),
  );

  const [isLoadingExpressions, setIsLoadingExpressions] = useState(false);

  const fetchExpressions = useCallback(async () => {
    setIsLoadingExpressions(true);

    try {
      const archetypeScores = {
        "Angelo Custode": commonForm.archetype_score_angelo_custode || 0,
        "Uomo comune": commonForm.archetype_score_uomo_comune || 0,
        Creatore: commonForm.archetype_score_creatore || 0,
        Mago: commonForm.archetype_score_mago || 0,
        Saggio: commonForm.archetype_score_saggio || 0,
        Esploratore: commonForm.archetype_score_esploratore || 0,
        Sovrano: commonForm.archetype_score_sovrano || 0,
        Innocente: commonForm.archetype_score_innocente || 0,
        Eroe: commonForm.archetype_score_eroe || 0,
        Ribelle: commonForm.archetype_score_ribelle || 0,
        Amante: commonForm.archetype_score_amante || 0,
        Burlone: commonForm.archetype_score_burlone || 0,
      };

      // API now returns objects with expression and match_percentage
      const result: ExpressionWithMatch[] = await getExpressions({
        archetype_values: archetypeScores,
      });

      // Get all checked expressions to preserve them
      const checkedExpressions = expressions.filter((item) => item.checked);

      // Create a set of new expression texts for quick lookup
      const newExpressionsSet = new Set(
        result
          .filter((item) => item.expression.trim() !== "")
          .map((item) => item.expression),
      );

      // Create a map of existing expressions for quick lookup
      const existingExpressionsMap = new Map(
        expressions.map((item) => [item.expression, item]),
      );

      // Combine new expressions with previously checked expressions
      setExpressions([
        // Add new expressions from the result
        ...result
          .filter((item) => item.expression.trim() !== "")
          .map((item, index) => {
            // If expression already exists, preserve its checked state
            const existingItem = existingExpressionsMap.get(item.expression);
            return {
              expression: item.expression,
              checked: existingItem ? existingItem.checked : false,
              initialOrder: index,
              matchPercentage: item.match_percentage, // Store the match percentage
            };
          }),
        // Add previously checked expressions that aren't in the new results
        ...checkedExpressions.filter(
          (item) => !newExpressionsSet.has(item.expression),
        ),
      ]);
    } catch (error) {
      // setExpressions([]);
    } finally {
      setIsLoadingExpressions(false);
    }
  }, [commonForm, expressions]);

  useEffect(() => {
    if ((isClientRelated || isEdit) && !firstFetchedExpressions) {
      setFirstFetchedExpressions(true);

      fetchExpressions();
    }
  }, [
    isEdit,
    user,
    isClientRelated,
    fetchExpressions,
    expressions,
    firstFetchedExpressions,
  ]);

  const getFullData = useCallback((): CreateBrandData | CreateBatchData => {
    // Current brand: for brands it's the id being edited, for batches it's the parent brand_id
    const currentBrandId =
      type === "brand" ? id : (commonForm as Batch).brand_id;

    // Ensure current brand is always included in phrasebook_brands
    const phrasebookBrands = commonForm.settings?.phrasebook_brands || [];
    const finalPhrasebookBrands =
      currentBrandId && !phrasebookBrands.includes(currentBrandId)
        ? [...phrasebookBrands, currentBrandId]
        : phrasebookBrands;

    const base = {
      ...(commonForm as CreateBrandData | CreateBatchData),
      gdrive_folder_id: commonForm.gdrive_folder_id || "",
      bullet_points_order:
        commonForm.bullet_points_order?.filter((bulletPointType) =>
          Object.values(BulletPointType).includes(bulletPointType),
        ) || [],
      words_to_avoid: commonForm.words_to_avoid || [],
      expressions: expressions
        .filter((e) => e.checked)
        .map((e) => e.expression),
      settings: {
        ...(commonForm.settings || defaultSettings),
        phrasebook_brands: finalPhrasebookBrands,
      },
    };

    if (type === "batch") {
      return {
        ...base,
        season: (commonForm as Batch)?.season || "",
        collection_name: (commonForm as Batch)?.collection_name || "",
      };
    }

    return base;
  }, [commonForm, expressions, type, id]);

  const clearValidationErrors = useCallback(
    (key: string | string[]) => {
      const newErrors = { ...validationErrors };

      if (Array.isArray(key)) {
        key.forEach((k) => {
          newErrors[k] = "";
        });
      } else {
        // Handle keys that may be nested with dots or partial matches
        Object.keys(newErrors).forEach((errorKey) => {
          if (
            errorKey === key ||
            errorKey.startsWith(`${key}.`) ||
            errorKey.endsWith(`.${key}`)
          ) {
            newErrors[errorKey] = "";
          }
        });
      }

      // Remove any empty strings
      Object.keys(newErrors).forEach((errorKey) => {
        if (newErrors[errorKey] === "") {
          delete newErrors[errorKey];
        }
      });

      setValidationErrors(newErrors);
    },
    [validationErrors],
  );

  // Handle data source mode change
  const handleDataSourceModeChange = useCallback(
    (newMode: DataSourceMode) => {
      setDataSourceMode(newMode);
      clearValidationErrors("gdrive_folder_id");

      if (newMode === "manual") {
        // Clear the gdrive_folder_id when switching to manual mode
        setCommonForm((prev) => ({
          ...prev,
          gdrive_folder_id: undefined,
        }));
      }
    },
    [clearValidationErrors],
  );

  // Validate the entire form using schema
  const validateForm = useCallback(async () => {
    try {
      const nextValidationErrors: Record<string, string> = {};

      // Validate with Zod - parse() will throw if validation fails
      const result = brandBatchSchema.safeParse({
        ...getFullData(),
        $type: type,
      });

      // For non-client users using GDrive mode, gdrive_folder_id is required
      // For manual mode, gdrive_folder_id is not required
      // Skip validation for batch edit (readonly mode - can't change data source)
      const isBatchEdit = type === "batch" && isEdit;
      const hasGDriveFolder =
        commonForm.gdrive_folder_id &&
        commonForm.gdrive_folder_id.trim() !== "";

      if (
        !isClientRelated &&
        !isBatchEdit &&
        dataSourceMode === "gdrive" &&
        !hasGDriveFolder
      ) {
        nextValidationErrors["gdrive_folder_id"] =
          "Google Drive Folder is required";
      }

      if (type === "batch" && isEdit) {
        const commonFormSettings = commonForm.settings;

        if (initialSettings && commonFormSettings) {
          const initialGroups = getContentGroups(initialSettings);
          const currentGroups = getContentGroups(commonFormSettings);

          currentGroups.forEach((cg, idx) => {
            const ig = initialGroups.find((g) => g.id === cg.id);
            if (!ig || !ig.enabled || !cg.enabled) return;
            const fields = ["minPhrases", "maxPhrases", "minCharacters", "maxCharacters", "minWords", "maxWords"] as const;
            fields.forEach((field) => {
              const initialValue = ig.phrases[field];
              const currentValue = cg.phrases[field];
              if (initialValue !== undefined && currentValue !== undefined && currentValue < initialValue) {
                nextValidationErrors[
                  `settings.content_groups.${idx}.phrases.${field}`
                ] = `Cannot be lower than the initial value (${initialValue})`;
              }
            });
          });
        }
      }

      if (!result.success) {
        // Format Zod errors into path-based errors similar to the Yup format
        result.error.issues.forEach((issue) => {
          // Join the path array to create a string path (e.g., "settings.title.minPhrases")
          const path = issue.path.join(".");
          if (path) {
            nextValidationErrors[path] = issue.message;
          }
        });
      }

      if (Object.keys(nextValidationErrors).length > 0) {
        setValidationErrors(nextValidationErrors);

        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  }, [
    getFullData,
    type,
    commonForm.settings,
    commonForm.gdrive_folder_id,
    isEdit,
    isClientRelated,
    initialSettings,
    dataSourceMode,
  ]);

  // const handleBulletPointsOrderChange = (
  //   newBulletPointsOrder: BulletPointType[]
  // ) => {
  //   const validBulletPointsOrder = newBulletPointsOrder.filter(
  //     (bulletPointType) =>
  //       Object.values(BulletPointType).includes(bulletPointType)
  //   );

  //   setCommonForm({
  //     ...commonForm,
  //     bullet_points_order: validBulletPointsOrder,
  //   });

  //   clearValidationErrors("bullet_points_order");
  // };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showUnarchiveModal, setShowUnarchiveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Check if the item is archived
  const isArchived = !!(
    data as {
      deleted_at: string;
    }
  )?.deleted_at;

  const handleUnarchive = async () => {
    try {
      if (type === "brand") {
        await unarchiveBrand({ brandId: id as string });
      } else {
        await unarchiveBatch({ batchId: id as string });
      }

      // Invalidate the cache to refresh the list
      invalidate({
        resource: type,
        invalidates: ["list", "detail"],
        id: id,
      });

      setShowUnarchiveModal(false);
      onClose();
    } catch (error) {
      console.error("Error unarchiving:", error);
    }
  };

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Validate the form using Yup schema
    const isValid = await validateForm();

    if (!isValid) {
      return;
    }

    setIsSubmitting(true);

    try {
      const data: CreateBrandData | CreateBatchData = getFullData();

      if (isEdit) {
        await update({
          resource: type,
          id,
          values: data,
        });
      } else {
        await create({
          resource: type,
          values: data,
        });
      }

      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const errorsCount = Object.keys(validationErrors).length;
  const hasErrors = errorsCount > 0;

  return (
    <>
      {!isClientRelated && type === "batch" && (
        <GDriveFolderPickerDialog
          open={showFolderDialog}
          brandId={(data as CreateBatchData)?.brand_id}
          brandName={brand?.name || ""}
          onDone={() => {
            setShowFolderDialog(false);
            // Fetch the updated brand to get the folders
            fetchBrand((data as CreateBatchData)?.brand_id);
          }}
          onCancel={() => {
            setShowFolderDialog(false);
          }}
        />
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Client Owned Toggle - Admin/Manager only, Batch only */}
        {type === "batch" && (user?.isAdmin || user?.isManager) && (
          <Paper elevation={0} sx={{ p: 2, border: "1px solid #e0e0e0" }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Batch Ownership
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={(commonForm as Batch).client_owned ?? false}
                  onChange={(e) => {
                    setCommonForm({
                      ...commonForm,
                      client_owned: e.target.checked,
                    });
                  }}
                />
              }
              label="Client-owned batch"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {(commonForm as Batch).client_owned ? (
                <>
                  <strong>Client-owned:</strong> This batch belongs to the
                  client's organization. Only users within the same organization
                  can access it.
                </>
              ) : (
                <>
                  <strong>Internal:</strong> This batch is managed internally by
                  the team (admin, manager, copywriter). Clients cannot access
                  this batch.
                </>
              )}
            </Typography>
          </Paper>
        )}

        <GeneralSettings
          type={type}
          validationErrors={validationErrors}
          fields={
            [
              {
                type: "text",
                key: "name",
                label: type === "brand" ? "Nome Brand" : "Nome Batch",
                value: commonForm.name,
                required: true,
              },
              ...(type === "brand"
                ? [
                    {
                      type: "text",
                      key: "logo_name",
                      label: "Nome Logo",
                      value: (commonForm as Brand).logo_name,
                      required: true,
                    },
                  ]
                : []),
              ...(type === "batch"
                ? [
                    {
                      type: "text",
                      key: "season",
                      label: "Stagione",
                      value: (commonForm as Batch).season,
                      required: false,
                    },
                  ]
                : []),
              ...(type === "batch"
                ? [
                    {
                      type: "text",
                      key: "collection_name",
                      label: "Collection name",
                      value: (commonForm as Batch).collection_name,
                      required: false,
                    },
                  ]
                : []),
              {
                type: "select",
                options: Object.values(LanguageCode).map((code) => ({
                  value: code,
                  label: languageCodeToLabel(code),
                })),
                key: "original_language",
                label: "Language",
                value: commonForm.original_language,
                required: true,
                disabled: type === "batch" && isEdit,
              },
              ...(!isClientRelated
                ? [
                    {
                      type: "select",
                      options: [
                        { value: "SECOND_PERSON", label: "Second Person" },
                        { value: "THIRD_PERSON", label: "Third Person" },
                        {
                          value: "FIRST_PERSON_PLURAL",
                          label: "First Person Plural",
                        },
                        {
                          value: "SECOND_PERSON_PLURAL",
                          label: "Second Person Plural",
                        },
                      ],
                      key: "narrative_perspective",
                      label: "Narrative Perspective",
                      value: commonForm.narrative_perspective,
                      required: true,
                    },
                  ]
                : []),
            ].filter((field) => field.type !== undefined) as CommonFormField[]
          }
          onChange={(name, value) => {
            clearValidationErrors(name);

            setCommonForm({
              ...commonForm,
              [name]: value,
            });
          }}
        />

        {/* Data Source Mode selector - only for non-client users */}
        {!isClientRelated && (
          <DataSourceModeSelector
            mode={dataSourceMode}
            onChange={handleDataSourceModeChange}
            readonly={type === "batch" && isEdit}
            folders={batchesFolders}
            selectedFolderId={commonForm.gdrive_folder_id}
            onFolderChange={(folderId) => {
              clearValidationErrors("gdrive_folder_id");
              setCommonForm({
                ...commonForm,
                gdrive_folder_id: folderId,
              });
            }}
            folderError={validationErrors["gdrive_folder_id"]}
            showBrandFolderPicker={
              type === "batch" && !isEdit && !brand?.gdrive_folder_id
            }
            onBrandFolderPickerClick={() => setShowFolderDialog(true)}
          />
        )}

        {type === "brand" && (
          <Monograms
            monograms={commonForm.monograms || []}
            onChange={(newMonograms: Monogram[]) => {
              clearValidationErrors("monograms");

              setCommonForm({
                ...commonForm,
                monograms: newMonograms,
              });
            }}
            validationErrors={validationErrors}
          />
        )}

        {allowAIGeneration && !isClientRelated && (
          <ArchetypeScores
            scores={commonForm as Record<string, number>}
            onChange={(key, value) => {
              clearValidationErrors(key);

              setCommonForm({
                ...commonForm,
                [key]: value,
              });
            }}
          />
        )}

        {allowAIGeneration && !isClientRelated && (
          <ExpressionSelector
            expressions={expressions}
            isLoading={isLoadingExpressions}
            validationErrors={validationErrors}
            onChange={(newExpressions) => {
              setExpressions([...newExpressions]);

              clearValidationErrors("expressions");
            }}
            onFetchExpressions={fetchExpressions}
          />
        )}

        {!isClientRelated && (
          <ContentGroupsSettings
            groups={getContentGroups(commonForm.settings)}
            validationErrors={validationErrors}
            isEdit={isEdit}
            isBatch={type === "batch"}
            initialGroups={initialSettings ? getContentGroups(initialSettings) : undefined}
            onChange={(groups) => {
              // Clear all content_groups errors
              const keysToClean = Object.keys(validationErrors).filter(
                (k) =>
                  k.startsWith("settings.content_groups") ||
                  k.startsWith("settings.title") ||
                  k.startsWith("settings.shortDescription") ||
                  k.startsWith("settings.regularDescription") ||
                  k.startsWith("settings.longDescription") ||
                  k.startsWith("settings.bulletPoints"),
              );
              if (keysToClean.length > 0) {
                clearValidationErrors(keysToClean);
              }

              setCommonForm({
                ...commonForm,
                settings: {
                  ...(commonForm.settings || defaultSettings),
                  content_groups: groups,
                },
              });
            }}
          />
        )}

        {!isClientRelated && (
          <ExportRulesSettings
            rules={commonForm.settings?.export_rules || []}
            groups={getContentGroups(commonForm.settings)}
            validationErrors={validationErrors}
            onChange={(rules) => {
              setCommonForm({
                ...commonForm,
                settings: {
                  ...(commonForm.settings || defaultSettings),
                  export_rules: rules,
                },
              });
            }}
          />
        )}

        {allowAIGeneration && !isClientRelated && (
          <BrandInstructions
            settings={commonForm.settings || defaultSettings}
            validationErrors={validationErrors}
            onChange={(key: string, newSettings: BrandBatchSettings) => {
              clearValidationErrors(key);

              setCommonForm({
                ...commonForm,
                settings: newSettings,
              });
            }}
          />
        )}

        {allowAIGeneration && !isClientRelated && (
          <Instructions
            settings={commonForm.settings || defaultSettings}
            onChange={(key: string, newSettings: BrandBatchSettings) => {
              clearValidationErrors(key);

              setCommonForm({
                ...commonForm,
                settings: newSettings,
              });
            }}
          />
        )}

        {availableTitleTemplatePatterns && allowAIGeneration && (
          <TitleTemplates
            availableTemplates={availableTitleTemplatePatterns}
            selectedTemplates={commonForm.settings?.title_templates || []}
            onChange={(newSelectedTemplates) => {
              setCommonForm({
                ...commonForm,
                settings: {
                  ...(commonForm.settings || defaultSettings),
                  title_templates: newSelectedTemplates,
                },
              });
            }}
          />
        )}

        {!isClientRelated && (
          <WordsToAvoid
            words={commonForm.words_to_avoid || []}
            onChange={(newWords: string[]) => {
              clearValidationErrors("words_to_avoid");

              setCommonForm({
                ...commonForm,
                words_to_avoid: newWords,
              });
            }}
          />
        )}

        {brandsWithGlobalPhrases.length > 0 &&
          (() => {
            // Current brand: for brands it's the id being edited, for batches it's the parent brand_id
            const currentBrandId =
              type === "brand" ? id : (commonForm as Batch).brand_id;
            // Show organization filter alert for admins/managers editing client-owned batches
            const isOrganizationFiltered =
              !isClientRelated && type === "batch" && !!brand?.organization_id;

            return (
              <PhrasebookBrands
                availableBrands={brandsWithGlobalPhrases}
                selectedBrands={commonForm.settings?.phrasebook_brands || []}
                currentBrandId={currentBrandId}
                isOrganizationFiltered={isOrganizationFiltered}
                onChange={(newSelectedBrands) => {
                  const newSettings = {
                    ...(commonForm.settings || defaultSettings),
                    phrasebook_brands: newSelectedBrands,
                  };

                  setCommonForm({
                    ...commonForm,
                    settings: newSettings,
                  });
                }}
              />
            );
          })()}

        {allowTranslations && (
          <LanguagesToTranslate
            languagesToTranslate={
              commonForm.settings?.languages_to_translate || []
            }
            excludedLanguages={[
              ...(commonForm.settings?.lara_languages || []),
              ...(commonForm.original_language
                ? [commonForm.original_language]
                : []),
            ]}
            hasAiTranslationLanguages={
              (commonForm.settings?.lara_languages || []).length > 0
            }
            setLanguagesToTranslate={(newLanguagesToTranslate) => {
              const newSettings = {
                ...(commonForm.settings || defaultSettings),
                languages_to_translate: newLanguagesToTranslate,
              };

              setCommonForm({
                ...commonForm,
                settings: newSettings,
              });
            }}
          />
        )}

        {allowLaraTranslation && (
          <LaraLanguages
            laraLanguages={commonForm.settings?.lara_languages || []}
            excludedLanguages={[
              ...(commonForm.settings?.languages_to_translate || []),
              ...(commonForm.original_language
                ? [commonForm.original_language]
                : []),
            ]}
            hasHumanTranslationLanguages={
              (commonForm.settings?.languages_to_translate || []).length > 0
            }
            setLaraLanguages={(newLaraLanguages) => {
              const newSettings = {
                ...(commonForm.settings || defaultSettings),
                lara_languages: newLaraLanguages,
              };

              setCommonForm({
                ...commonForm,
                settings: newSettings,
              });
            }}
          />
        )}

        {!isClientRelated && (
          <LaraGlossaries
            availableGlossaries={laraResources.glossaries}
            selectedGlossaries={commonForm.settings?.lara_glossaries || []}
            setSelectedGlossaries={(ids) => {
              setCommonForm({
                ...commonForm,
                settings: {
                  ...(commonForm.settings || defaultSettings),
                  lara_glossaries: ids,
                },
              });
            }}
            isLoading={isLoadingLaraResources}
          />
        )}

        {!isClientRelated && (
          <LaraMemories
            availableMemories={laraResources.memories}
            selectedMemories={commonForm.settings?.lara_memories || []}
            setSelectedMemories={(ids) => {
              setCommonForm({
                ...commonForm,
                settings: {
                  ...(commonForm.settings || defaultSettings),
                  lara_memories: ids,
                },
              });
            }}
            isLoading={isLoadingLaraResources}
          />
        )}

        {!isClientRelated && (
          <LaraInstructions
            instructions={commonForm.settings?.lara_instructions || []}
            setInstructions={(instructions) => {
              setCommonForm({
                ...commonForm,
                settings: {
                  ...(commonForm.settings || defaultSettings),
                  lara_instructions: instructions,
                },
              });
            }}
          />
        )}

        {!isClientRelated && (
          <LaraStyle
            style={commonForm.settings?.lara_style || "faithful"}
            setStyle={(style) => {
              setCommonForm({
                ...commonForm,
                settings: {
                  ...(commonForm.settings || defaultSettings),
                  lara_style: style,
                },
              });
            }}
          />
        )}

        {/* <BulletPointsOrder
          bulletPointsOrder={commonForm.bullet_points_order || []}
          setBulletPointsOrder={handleBulletPointsOrderChange}
        /> */}
      </Box>

      {hasErrors && (
        <Alert sx={{ mt: 2 }} severity="error">
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            {errorsCount} errors found. Scroll up to see details.
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {Object.entries(validationErrors).map(([key, msg]) => (
              <li key={key}>
                <Typography variant="body2" component="span">
                  <code>{key}</code>: {msg}
                </Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 1,
          mt: 4,
        }}
      >
        {isEdit ? (
          <Box sx={{ display: "flex", gap: 1 }}>
            {isArchived ? (
              <Button
                variant="contained"
                color="success"
                onClick={() => setShowUnarchiveModal(true)}
                disabled={isSubmitting}
              >
                Unarchive
              </Button>
            ) : (
              <Button
                variant="contained"
                color="warning"
                onClick={() => setShowArchiveModal(true)}
                disabled={isSubmitting}
              >
                Archive
              </Button>
            )}

            {((type === "brand" && user?.isAdmin) ||
              (type === "batch" && (user?.isManager || user?.isAdmin))) && (
              <Button
                variant="contained"
                color="error"
                onClick={() => setShowDeleteModal(true)}
                disabled={isSubmitting}
              >
                Delete
              </Button>
            )}

            {/* Unarchive Confirmation Modal */}
            <Dialog
              open={showUnarchiveModal}
              onClose={() => setShowUnarchiveModal(false)}
              aria-labelledby="unarchive-dialog-title"
              aria-describedby="unarchive-dialog-description"
            >
              <DialogTitle id="unarchive-dialog-title">
                Unarchive {type}
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="unarchive-dialog-description">
                  Are you sure you want to unarchive this {type}?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowUnarchiveModal(false)}>
                  Cancel
                </Button>
                <Button
                  color="success"
                  variant="contained"
                  onClick={handleUnarchive}
                >
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>

            {/* Archive Confirmation Modal */}
            <Dialog
              open={showArchiveModal}
              onClose={() => setShowArchiveModal(false)}
              aria-labelledby="archive-dialog-title"
              aria-describedby="archive-dialog-description"
            >
              <DialogTitle id="archive-dialog-title">
                Archive {type}
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="archive-dialog-description">
                  Are you sure you want to archive this {type}? It will be
                  marked as deleted but will remain in the database for future
                  recovery.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowArchiveModal(false)}>
                  Cancel
                </Button>
                <Button
                  color="warning"
                  variant="contained"
                  onClick={() => {
                    deleteFn({
                      resource: type,
                      id,
                      meta: { softDelete: true },
                    });
                    setShowArchiveModal(false);
                    onClose();
                  }}
                >
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog
              open={showDeleteModal}
              onClose={() => setShowDeleteModal(false)}
              aria-labelledby="delete-dialog-title"
              aria-describedby="delete-dialog-description"
            >
              <DialogTitle id="delete-dialog-title">Delete {type}</DialogTitle>
              <DialogContent>
                <DialogContentText id="delete-dialog-description">
                  Are you sure you want to delete this {type}? This action
                  cannot be undone and all associated data will be lost.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </Button>
                <Button
                  color="error"
                  variant="contained"
                  onClick={() => {
                    deleteFn({
                      resource: type,
                      id,
                    });
                    setShowDeleteModal(false);
                    onClose();
                  }}
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        ) : (
          <Box></Box>
        )}

        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button onClick={onClose}>Cancel</Button>

          <Button
            variant="contained"
            onClick={handleFormSubmit}
            disabled={isSubmitting || isArchived}
          >
            {isSubmitting ? "Processing..." : isEdit ? "Update" : "Create"}
          </Button>
        </Box>
      </Box>
    </>
  );
};
